import React, { useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Button,
  Card,
  CardBody,
  CardTitle,
  Alert,
  Label,
  Input,
  FormGroup,
} from "reactstrap" // Assuming Reactstrap for theme components
import axios from "axios" // For API calls
import apiURl, { baseURl } from "url"

const BusinessRegistrationModal = ({ isOpen, toggle, getAllBusinesses }) => {
  const [activeTab, setActiveTab] = useState(1)
  const [passedSteps, setPassedSteps] = useState([1])
  const [formData, setFormData] = useState({
    profile: {},
    pickups: [{}], // Start with one pickup
    banks: [{}], // Start with one bank
    documents: {},
    login: {},
  })
  const [files, setFiles] = useState({})
  const [submitLoading, setSubmitLoading] = useState(false)
  const [error, setError] = useState("")

  const cities = ["", "karachi", "lahore", "islamabad"] // Add more as needed
  const salesPersons = ["", "person1", "person2"] // Add more as needed

  const toggleTab = tab => {
    if (tab === activeTab) return

    // Validate current tab before switching
    if (tab > activeTab && !validateStep(activeTab)) {
      setError("Please fill all required fields before proceeding.")
      return
    }

    setError("")
    if (tab >= 1 && tab <= 6) {
      const modifiedSteps = [...new Set([...passedSteps, tab])]
      setActiveTab(tab)
      setPassedSteps(modifiedSteps)
    }
  }

  const updateFormData = (step, data) => {
    setFormData(prev => ({ ...prev, [step]: data }))
  }

  const handleInputChange = (e, step) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        [name]: value,
      },
    }))
  }

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0]
    setFiles(prev => ({ ...prev, [fieldName]: file }))
  }

  const addPickup = () => {
    setFormData(prev => ({
      ...prev,
      pickups: [...prev.pickups, {}],
    }))
  }

  const removePickup = index => {
    setFormData(prev => ({
      ...prev,
      pickups: prev.pickups.filter((_, i) => i !== index),
    }))
  }

  const addBank = () => {
    setFormData(prev => ({
      ...prev,
      banks: [...prev.banks, {}],
    }))
  }

  const removeBank = index => {
    setFormData(prev => ({
      ...prev,
      banks: prev.banks.filter((_, i) => i !== index),
    }))
  }

  const locateMe = index => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const coords = `${position.coords.latitude}, ${position.coords.longitude}`
          const newPickups = [...formData.pickups]
          newPickups[index].coordinates = coords
          updateFormData("pickups", newPickups)
        },
        error => {
          alert("Geolocation failed: " + error.message)
        }
      )
    } else {
      alert("Geolocation not supported")
    }
  }

  const validateStep = step => {
    // Basic validation - expand as needed
    switch (step) {
      case 1:
        const profileReq = [
          "businessName",
          "brandName",
          "pocName",
          "companyAddress",
          "phone1",
          "email",
          "cnic",
          "url",
          "productType",
          "expectedShipments",
          "city",
          "salesPerson",
          "nature",
        ]
        return profileReq.every(field => formData.profile[field])
      case 2:
        return (
          formData.pickups.length > 0 &&
          formData.pickups.every(p => p.address && p.city)
        )
      case 3:
        return (
          formData.banks.length > 0 &&
          formData.banks.every(b => b.bankName && b.iban)
        )
      case 4:
        return files.cnicFront && files.cnicBack
      case 5:
        return (
          formData.login.loginEmail &&
          formData.login.password &&
          formData.login.password.length >= 8
        )
      default:
        return true
    }
  }

  const nextStep = () => {
    if (!validateStep(activeTab)) {
      setError("Please fill all required fields in this step.")
      return
    }
    setError("")
    if (activeTab < 5) {
      toggleTab(activeTab + 1)
    } else {
      handleSubmit()
    }
  }

  const prevStep = () => {
    if (activeTab > 1) {
      toggleTab(activeTab - 1)
    }
  }

  const resetForm = () => {
    setActiveTab(1)
    setPassedSteps([1])
    setFormData({
      profile: {},
      pickups: [{}],
      banks: [{}],
      documents: {},
      login: {},
    })
    setFiles({})
    setError("")
    toggle()
  }

  const handleSubmit = async () => {
    setSubmitLoading(true)
    setError("")

    const submitData = new FormData()
    // Append profile
    Object.keys(formData.profile).forEach(key => {
      submitData.append(key, formData.profile[key])
    })
    // Append pickups and banks as JSON
    submitData.append("pickups", JSON.stringify(formData.pickups))
    submitData.append("banks", JSON.stringify(formData.banks))
    // Append login
    Object.keys(formData.login).forEach(key => {
      submitData.append(key, formData.login[key])
    })
    // Append files
    Object.keys(files).forEach(key => {
      if (files[key]) {
        submitData.append(key, files[key])
      }
    })

    try {
      const response = await axios.post(
        `${apiURl}/registerBusiness`,
        submitData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      )

      if (response.data.status === "success") {
        setActiveTab(6) // Show thanks step
        if (typeof getAllBusinesses === "function") {
          getAllBusinesses()
        }
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Submission failed. Please try again."
      )
    } finally {
      setSubmitLoading(false)
    }
  }

  const renderStepContent = () => {
    switch (activeTab) {
      case 1: // Profile Info
        return (
          <div className="space-y-2">
            <div className="row g-2">
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="businessName" className="small">
                    Business/Company Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.profile.businessName || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                    required
                  />
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="brandName" className="small">
                    Brand Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="brandName"
                    name="brandName"
                    value={formData.profile.brandName || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                    required
                  />
                </FormGroup>
              </div>
            </div>
            <FormGroup className="mb-1">
              <Label for="pocName" className="small">
                POC Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                id="pocName"
                name="pocName"
                value={formData.profile.pocName || ""}
                onChange={e => handleInputChange(e, "profile")}
                size="sm"
                required
              />
            </FormGroup>
            <FormGroup className="mb-1">
              <Label for="companyAddress" className="small">
                Company Address <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                id="companyAddress"
                name="companyAddress"
                value={formData.profile.companyAddress || ""}
                onChange={e => handleInputChange(e, "profile")}
                rows="1"
                size="sm"
                required
              />
            </FormGroup>
            <div className="row g-2">
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="phone1" className="small">
                    Phone Number 1 <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="tel"
                    id="phone1"
                    name="phone1"
                    value={formData.profile.phone1 || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                    required
                  />
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="phone2" className="small">
                    Phone Number 2
                  </Label>
                  <Input
                    type="tel"
                    id="phone2"
                    name="phone2"
                    value={formData.profile.phone2 || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                  />
                </FormGroup>
              </div>
            </div>
            <div className="row g-2">
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="email" className="small">
                    Email Address <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.profile.email || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                    required
                  />
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="cnic" className="small">
                    CNIC Number <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="cnic"
                    name="cnic"
                    value={formData.profile.cnic || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                    required
                  />
                </FormGroup>
              </div>
            </div>
            <div className="row g-2">
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="ntn" className="small">
                    NTN Number
                  </Label>
                  <Input
                    type="text"
                    id="ntn"
                    name="ntn"
                    value={formData.profile.ntn || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                  />
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="strn" className="small">
                    STRN Number
                  </Label>
                  <Input
                    type="text"
                    id="strn"
                    name="strn"
                    value={formData.profile.strn || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                  />
                </FormGroup>
              </div>
            </div>
            <FormGroup className="mb-1">
              <Label for="url" className="small">
                URL (website/social media links){" "}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="url"
                id="url"
                name="url"
                value={formData.profile.url || ""}
                onChange={e => handleInputChange(e, "profile")}
                size="sm"
                required
              />
            </FormGroup>
            <div className="row g-2">
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="productType" className="small">
                    Product type/Niche <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="productType"
                    name="productType"
                    value={formData.profile.productType || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                    required
                  />
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="expectedShipments" className="small">
                    Expected Monthly Shipments{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="number"
                    id="expectedShipments"
                    name="expectedShipments"
                    value={formData.profile.expectedShipments || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                    min="0"
                    required
                  />
                </FormGroup>
              </div>
            </div>
            <div className="row g-2">
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="city" className="small">
                    City <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    id="city"
                    name="city"
                    value={formData.profile.city || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                    required
                  >
                    {cities.map(city => (
                      <option key={city} value={city}>
                        {city.charAt(0).toUpperCase() + city.slice(1) ||
                          "Select City"}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup className="mb-1">
                  <Label for="salesPerson" className="small">
                    Sales Person <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    id="salesPerson"
                    name="salesPerson"
                    value={formData.profile.salesPerson || ""}
                    onChange={e => handleInputChange(e, "profile")}
                    size="sm"
                    required
                  >
                    {salesPersons.map(person => (
                      <option key={person} value={person}>
                        {person.charAt(0).toUpperCase() + person.slice(1) ||
                          "Select Sales Person"}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
            </div>
            <FormGroup className="mb-1">
              <Label className="small">
                Nature of Account <span className="text-danger">*</span>
              </Label>
              <div className="d-flex gap-2 small">
                <FormGroup check className="mb-0">
                  <Label check className="small">
                    <Input
                      type="radio"
                      name="nature"
                      value="COD"
                      checked={formData.profile.nature === "COD"}
                      onClick={e => handleInputChange(e, "profile")}
                    />{" "}
                    COD
                  </Label>
                </FormGroup>
                <FormGroup check className="mb-0">
                  <Label check className="small">
                    <Input
                      type="radio"
                      name="nature"
                      value="Non-COD"
                      checked={formData.profile.nature === "Non-COD"}
                      onClick={e => handleInputChange(e, "profile")}
                    />{" "}
                    Non-COD
                  </Label>
                </FormGroup>
              </div>

              {formData.profile.nature === "Non-COD" && (
                <div className="ms-3 mt-1 small">
                  <FormGroup check className="mb-0">
                    <Label check className="small">
                      <Input
                        type="radio"
                        name="nonCodType"
                        value="domestic"
                        checked={formData.profile.nonCodType === "domestic"}
                        onClick={e => handleInputChange(e, "profile")}
                      />{" "}
                      Domestic
                    </Label>
                  </FormGroup>
                  <FormGroup check className="mb-0">
                    <Label check className="small">
                      <Input
                        type="radio"
                        name="nonCodType"
                        value="international"
                        checked={
                          formData.profile.nonCodType === "international"
                        }
                        onClick={e => handleInputChange(e, "profile")}
                      />{" "}
                      International
                    </Label>
                  </FormGroup>
                </div>
              )}
            </FormGroup>
          </div>
        )
      case 2: // Shipping Info
        return (
          <div>
            {formData.pickups.map((pickup, index) => (
              <div
                key={index}
                className="border p-2 mb-2 rounded"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <h6 className="text-primary mb-2 small">
                  Pickup Address #{index + 1}
                </h6>
                <FormGroup className="mb-1">
                  <Label className="small">Brand Name</Label>
                  <Input
                    type="text"
                    size="sm"
                    value={pickup.brandName || formData.profile.brandName || ""}
                    onChange={e => {
                      const newPickups = [...formData.pickups]
                      newPickups[index].brandName = e.target.value
                      updateFormData("pickups", newPickups)
                    }}
                  />
                </FormGroup>
                <FormGroup className="mb-1">
                  <Label className="small">Pickup Address</Label>
                  <Input
                    type="textarea"
                    rows="1"
                    size="sm"
                    value={pickup.address || ""}
                    onChange={e => {
                      const newPickups = [...formData.pickups]
                      newPickups[index].address = e.target.value
                      updateFormData("pickups", newPickups)
                    }}
                  />
                </FormGroup>
                <FormGroup className="mb-1">
                  <Label className="small">Google Coordinates</Label>
                  <div className="d-flex align-items-end gap-2">
                    <Input
                      type="text"
                      size="sm"
                      placeholder="Lat, Long"
                      value={pickup.coordinates || ""}
                      onChange={e => {
                        const newPickups = [...formData.pickups]
                        newPickups[index].coordinates = e.target.value
                        updateFormData("pickups", newPickups)
                      }}
                    />
                    <Button
                      color="info"
                      size="sm"
                      type="button"
                      onClick={() => locateMe(index)}
                    >
                      Locate me
                    </Button>
                  </div>
                </FormGroup>
                <div className="row g-2">
                  <div className="col-md-6">
                    <FormGroup className="mb-1">
                      <Label className="small">Pickup City</Label>
                      <Input
                        type="select"
                        size="sm"
                        value={pickup.city || ""}
                        onChange={e => {
                          const newPickups = [...formData.pickups]
                          newPickups[index].city = e.target.value
                          updateFormData("pickups", newPickups)
                        }}
                      >
                        {cities.map(city => (
                          <option key={city} value={city}>
                            {city.charAt(0).toUpperCase() + city.slice(1) ||
                              "Select City"}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-1">
                      <Label className="small">Person of Contact</Label>
                      <Input
                        type="text"
                        size="sm"
                        value={pickup.poc || ""}
                        onChange={e => {
                          const newPickups = [...formData.pickups]
                          newPickups[index].poc = e.target.value
                          updateFormData("pickups", newPickups)
                        }}
                      />
                    </FormGroup>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-md-6">
                    <FormGroup className="mb-1">
                      <Label className="small">Phone Number</Label>
                      <Input
                        type="tel"
                        size="sm"
                        value={pickup.phone || ""}
                        onChange={e => {
                          const newPickups = [...formData.pickups]
                          newPickups[index].phone = e.target.value
                          updateFormData("pickups", newPickups)
                        }}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-1">
                      <Label className="small">Email Address</Label>
                      <Input
                        type="email"
                        size="sm"
                        value={pickup.email || ""}
                        onChange={e => {
                          const newPickups = [...formData.pickups]
                          newPickups[index].email = e.target.value
                          updateFormData("pickups", newPickups)
                        }}
                      />
                    </FormGroup>
                  </div>
                </div>
                {index > 0 && (
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => removePickup(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button color="success" size="sm" onClick={addPickup}>
              Add Pickup Address
            </Button>
          </div>
        )
      case 3: // Finance Info
        return (
          <div>
            {formData.banks.map((bank, index) => (
              <div
                key={index}
                className="border p-2 mb-2 rounded"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <h6 className="text-primary mb-2 small">
                  Bank Details #{index + 1}
                </h6>
                <div className="row g-2">
                  <div className="col-md-6">
                    <FormGroup className="mb-1">
                      <Label className="small">Bank Name</Label>
                      <Input
                        type="text"
                        size="sm"
                        value={bank.bankName || ""}
                        onChange={e => {
                          const newBanks = [...formData.banks]
                          newBanks[index].bankName = e.target.value
                          updateFormData("banks", newBanks)
                        }}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-1">
                      <Label className="small">Branch Name</Label>
                      <Input
                        type="text"
                        size="sm"
                        value={bank.branchName || ""}
                        onChange={e => {
                          const newBanks = [...formData.banks]
                          newBanks[index].branchName = e.target.value
                          updateFormData("banks", newBanks)
                        }}
                      />
                    </FormGroup>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-md-6">
                    <FormGroup className="mb-1">
                      <Label className="small">Account Title</Label>
                      <Input
                        type="text"
                        size="sm"
                        value={bank.accountTitle || ""}
                        onChange={e => {
                          const newBanks = [...formData.banks]
                          newBanks[index].accountTitle = e.target.value
                          updateFormData("banks", newBanks)
                        }}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup className="mb-1">
                      <Label className="small">Branch City</Label>
                      <Input
                        type="select"
                        size="sm"
                        value={bank.branchCity || ""}
                        onChange={e => {
                          const newBanks = [...formData.banks]
                          newBanks[index].branchCity = e.target.value
                          updateFormData("banks", newBanks)
                        }}
                      >
                        {cities.map(city => (
                          <option key={city} value={city}>
                            {city.charAt(0).toUpperCase() + city.slice(1) ||
                              "Select City"}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </div>
                </div>
                <FormGroup className="mb-1">
                  <Label className="small">IBAN</Label>
                  <Input
                    type="text"
                    size="sm"
                    value={bank.iban || ""}
                    onChange={e => {
                      const newBanks = [...formData.banks]
                      newBanks[index].iban = e.target.value
                      updateFormData("banks", newBanks)
                    }}
                  />
                </FormGroup>
                {index > 0 && (
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => removeBank(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button color="success" size="sm" onClick={addBank}>
              Add Bank Details
            </Button>
          </div>
        )
      case 4: // Documents
        return (
          <div className="row g-2">
            <div className="col-md-6">
              <FormGroup className="mb-1">
                <Label className="small">Picture of CNIC (Front)</Label>
                <Input
                  type="file"
                  size="sm"
                  onChange={e => handleFileChange(e, "cnicFront")}
                  accept=".pdf,.jpeg,.jpg,.png,.docx"
                />
                {files.cnicFront && (
                  <small className="text-muted d-block mt-1">
                    {files.cnicFront.name}
                  </small>
                )}
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup className="mb-1">
                <Label className="small">Picture of CNIC (Back)</Label>
                <Input
                  type="file"
                  size="sm"
                  onChange={e => handleFileChange(e, "cnicBack")}
                  accept=".pdf,.jpeg,.jpg,.png,.docx"
                />
                {files.cnicBack && (
                  <small className="text-muted d-block mt-1">
                    {files.cnicBack.name}
                  </small>
                )}
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup className="mb-1">
                <Label className="small">NTN Certificate</Label>
                <Input
                  type="file"
                  size="sm"
                  onChange={e => handleFileChange(e, "ntnDoc")}
                  accept=".pdf,.jpeg,.jpg,.png,.docx"
                />
                {files.ntnDoc && (
                  <small className="text-muted d-block mt-1">
                    {files.ntnDoc.name}
                  </small>
                )}
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup className="mb-1">
                <Label className="small">Blank Cheque</Label>
                <Input
                  type="file"
                  size="sm"
                  onChange={e => handleFileChange(e, "blankCheque")}
                  accept=".pdf,.jpeg,.jpg,.png,.docx"
                />
                {files.blankCheque && (
                  <small className="text-muted d-block mt-1">
                    {files.blankCheque.name}
                  </small>
                )}
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup className="mb-1">
                <Label className="small">Shipper Pic</Label>
                <Input
                  type="file"
                  size="sm"
                  onChange={e => handleFileChange(e, "shipperPic")}
                  accept=".pdf,.jpeg,.jpg,.png,.docx"
                />
                {files.shipperPic && (
                  <small className="text-muted d-block mt-1">
                    {files.shipperPic.name}
                  </small>
                )}
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup className="mb-1">
                <Label className="small">Shop Fascia</Label>
                <Input
                  type="file"
                  size="sm"
                  onChange={e => handleFileChange(e, "shopFascia")}
                  accept=".pdf,.jpeg,.jpg,.png,.docx"
                />
                {files.shopFascia && (
                  <small className="text-muted d-block mt-1">
                    {files.shopFascia.name}
                  </small>
                )}
              </FormGroup>
            </div>
          </div>
        )
      case 5: // Login Info
        return (
          <div className="space-y-2">
            <FormGroup className="mb-1">
              <Label for="loginEmail" className="small">
                Email Address <span className="text-danger">*</span>
              </Label>
              <Input
                type="email"
                id="loginEmail"
                name="loginEmail"
                value={formData.login.loginEmail || ""}
                onChange={e => handleInputChange(e, "login")}
                size="sm"
                required
              />
            </FormGroup>
            <FormGroup className="mb-1">
              <Label for="password" className="small">
                Password <span className="text-danger">*</span>
              </Label>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.login.password || ""}
                onChange={e => handleInputChange(e, "login")}
                size="sm"
                minLength={8}
                required
              />
            </FormGroup>
          </div>
        )
      case 6: // Thanks
        return (
          <div className="text-center p-3">
            <h3 className="text-success mb-2">Thank You!</h3>
            <p className="text-muted small mb-3">
              Your registration has been submitted successfully. We will contact
              you soon.
            </p>
            <Button color="primary" size="sm" onClick={resetForm}>
              Close
            </Button>
          </div>
        )
      default:
        return null
    }
  }

  if (!isOpen) return null

  return (
    <Modal isOpen={isOpen} toggle={resetForm} size="lg">
      <Form
        onSubmit={e => {
          e.preventDefault()
          nextStep()
        }}
      >
        <ModalHeader toggle={resetForm}>Register New Business</ModalHeader>
        <ModalBody className="p-2">
          {error && (
            <Alert color="danger" className="mb-2">
              {error}
            </Alert>
          )}
          <Card className="border-0">
            <CardBody className="p-2">
              <div className="wizard clearfix">
                {/* Stepper Navigation */}
                <ul className="nav nav-tabs nav-justified mb-3" role="tablist">
                  {[
                    "Profile Info",
                    "Shipping Info",
                    "Finance Info",
                    "Documents",
                    "Login Info",
                  ].map((label, index) => (
                    <li
                      key={index}
                      className={`nav-item ${
                        activeTab === index + 1
                          ? "active"
                          : passedSteps.includes(index + 1)
                          ? "done"
                          : ""
                      }`}
                    >
                      <a
                        className="nav-link d-flex flex-column align-items-center small"
                        href="#"
                        onClick={e => {
                          e.preventDefault()
                          if (
                            index + 1 <= activeTab ||
                            validateStep(activeTab)
                          ) {
                            toggleTab(index + 1)
                          } else {
                            setError(
                              "Please complete current step before moving ahead."
                            )
                          }
                        }}
                      >
                        <span
                          className={`step rounded-circle d-flex align-items-center justify-content-center mb-1 ${
                            activeTab === index + 1
                              ? "bg-primary text-white"
                              : passedSteps.includes(index + 1)
                              ? "bg-success text-white"
                              : "bg-light"
                          }`}
                          style={{
                            width: "25px",
                            height: "25px",
                            fontSize: "0.75rem",
                          }}
                        >
                          {index + 1}
                        </span>
                        <span className="small">{label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
                {/* Step Content */}
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    {renderStepContent()}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </ModalBody>
        {activeTab < 6 && (
          <ModalFooter className="p-2">
            <Button
              color="secondary"
              size="sm"
              onClick={prevStep}
              disabled={activeTab === 1}
            >
              Previous
            </Button>
            <Button color="primary" size="sm" type="submit">
              {activeTab === 5 ? "Submit" : "Next"}
            </Button>
          </ModalFooter>
        )}
      </Form>
      {submitLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div className="text-center text-white">
            <i
              className="fas fa-spinner fa-spin"
              style={{ fontSize: "2rem", marginBottom: "0.5rem" }}
            />
            <p className="small mb-0">Submitting...</p>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default BusinessRegistrationModal
